import { calculateAge } from './taxCalculations';
import { api } from '@/api/apiClient';

/**
 * Calculate benefit premium using RatingTable data
 */
export async function calculateBenefitPremium(product, employee, familyMembers = [], coverageLevel = 'employee_only') {
  // Fetch rating table for this benefit type
  const ratingTables = await api.entities.RatingTable.filter({
    benefit_type: product.benefit_type
  });
  
  if (ratingTables.length === 0) {
    // Fallback to product base rate if no rating table exists
    return parseFloat(product.base_premium || 0);
  }
  
  const ratingTable = ratingTables[0];
  const { rating_method, rates } = ratingTable;
  
  switch (rating_method) {
    case 'flat':
      return calculateFlatRate(rates, coverageLevel);
      
    case 'age_banded':
      return calculateAgeBanded(rates, employee, coverageLevel);
      
    case 'age_gender':
      return calculateAgeGender(rates, employee, familyMembers, coverageLevel);
      
    case 'tobacco':
      return calculateTobaccoRated(rates, employee, coverageLevel);
      
    case 'geographic':
      return calculateGeographic(rates, employee, coverageLevel);
      
    default:
      return parseFloat(product.base_premium || 0);
  }
}

/**
 * Calculate benefit premium with detailed breakdown
 */
export async function calculateBenefitPremiumWithDetails(product, employee, familyMembers = [], coverageLevel = 'employee_only') {
  const details = {
    method: 'base_premium',
    basePremium: parseFloat(product.base_premium || 0),
    coverageLevel,
    breakdown: []
  };

  // Fetch rating table for this benefit type
  const ratingTables = await api.entities.RatingTable.filter({
    benefit_type: product.benefit_type
  });
  
  if (ratingTables.length === 0) {
    details.breakdown.push(`No rating table found for ${product.benefit_type}`);
    details.breakdown.push(`Using product base premium: $${details.basePremium.toFixed(2)}`);
    return { premium: details.basePremium, details };
  }
  
  const ratingTable = ratingTables[0];
  const { rating_method, rates } = ratingTable;
  details.method = rating_method;
  details.ratingTableName = ratingTable.name;
  
  let premium = 0;
  
  switch (rating_method) {
    case 'flat':
      premium = calculateFlatRate(rates, coverageLevel);
      details.breakdown.push(`Flat rate for ${coverageLevel}: $${premium.toFixed(2)}`);
      break;
      
    case 'age_banded':
      if (employee?.date_of_birth) {
        const age = calculateAge(employee.date_of_birth);
        details.employeeAge = age;
        const ageBands = Object.keys(rates).map(Number).sort((a, b) => a - b);
        let ageBand = ageBands.find(band => age <= band) || ageBands[ageBands.length - 1];
        details.ageBand = ageBand;
        details.breakdown.push(`Employee age: ${age}`);
        details.breakdown.push(`Age band: ${ageBand}`);
        details.breakdown.push(`Base rate: $${rates[ageBand]}`);
      }
      premium = calculateAgeBanded(rates, employee, coverageLevel);
      break;
      
    case 'age_gender':
      if (employee?.date_of_birth && employee?.gender) {
        const age = calculateAge(employee.date_of_birth);
        details.employeeAge = age;
        details.employeeGender = employee.gender;
        details.breakdown.push(`Employee: Age ${age}, Gender ${employee.gender}`);
        
        // Debug: show rate structure
        const genderRates = rates[employee.gender];
        if (genderRates) {
          const ageBands = Object.keys(genderRates).map(Number).sort((a, b) => a - b);
          details.breakdown.push(`Available age bands: ${ageBands.join(', ')}`);
          const matchingBand = ageBands.find(band => age <= band);
          if (matchingBand) {
            details.breakdown.push(`Matched age band: ${matchingBand}`);
            details.breakdown.push(`Rate for band: $${genderRates[matchingBand]}`);
          } else {
            details.breakdown.push(`No matching age band found`);
          }
        } else {
          details.breakdown.push(`No rates found for gender: ${employee.gender}`);
          details.breakdown.push(`Available genders in rates: ${Object.keys(rates).join(', ')}`);
        }
      } else {
        if (!employee?.date_of_birth) details.breakdown.push(`Missing employee date_of_birth`);
        if (!employee?.gender) details.breakdown.push(`Missing employee gender`);
      }
      premium = calculateAgeGender(rates, employee, familyMembers, coverageLevel);
      break;
      
    case 'tobacco':
      if (employee?.date_of_birth) {
        const age = calculateAge(employee.date_of_birth);
        details.employeeAge = age;
        details.tobaccoUser = employee.tobacco_user || false;
        details.breakdown.push(`Employee age: ${age}`);
        details.breakdown.push(`Tobacco user: ${details.tobaccoUser ? 'Yes' : 'No'}`);
      }
      premium = calculateTobaccoRated(rates, employee, coverageLevel);
      break;
      
    case 'geographic':
      details.state = employee?.state || employee?.state_of_residence;
      details.breakdown.push(`State: ${details.state || 'Unknown'}`);
      premium = calculateGeographic(rates, employee, coverageLevel);
      break;
      
    default:
      premium = parseFloat(product.base_premium || 0);
      details.breakdown.push(`Unknown rating method: ${rating_method}`);
      details.breakdown.push(`Using base premium: $${premium.toFixed(2)}`);
  }
  
  details.breakdown.push(`Coverage level: ${coverageLevel}`);
  
  // If calculation returned 0, fall back to base premium
  if (premium === 0 && details.basePremium > 0) {
    details.breakdown.push(`⚠️ Calculation returned $0, using base premium instead`);
    premium = details.basePremium;
  }
  
  details.breakdown.push(`Final premium: $${premium.toFixed(2)}/month`);
  
  return { premium, details };
}

/**
 * Flat rate pricing by tier
 */
function calculateFlatRate(rates, coverageLevel) {
  return rates[coverageLevel] || rates['employee_only'] || 0;
}

/**
 * Age-banded pricing from rating table
 */
function calculateAgeBanded(rates, employee, coverageLevel) {
  if (!employee.date_of_birth) return 0;
  
  const age = calculateAge(employee.date_of_birth);
  
  // Find matching age band - rates are keyed by max age (e.g., {39: 10.50, 49: 15.25})
  let rate = 0;
  const ageBands = Object.keys(rates).map(Number).sort((a, b) => a - b);
  
  for (const maxAge of ageBands) {
    if (age <= maxAge) {
      rate = rates[maxAge];
      break;
    }
  }
  
  // If age exceeds all bands, use highest band
  if (rate === 0 && ageBands.length > 0) {
    rate = rates[ageBands[ageBands.length - 1]];
  }
  
  // Apply family multiplier (assuming standard 1.8x for spouse, 1.5x for children, 2.5x for family)
  const familyMultipliers = {
    employee_only: 1.0,
    employee_spouse: 1.8,
    employee_children: 1.5,
    family: 2.5
  };
  
  return rate * (familyMultipliers[coverageLevel] || 1.0);
}

/**
 * Age and gender-based pricing from rating table
 */
function calculateAgeGender(rates, employee, familyMembers, coverageLevel) {
  if (!employee?.date_of_birth || !employee?.gender) {
    console.warn('Age/Gender calculation requires date_of_birth and gender', { employee });
    return 0;
  }
  
  const age = calculateAge(employee.date_of_birth);
  
  // Get rate for employee - rates structure: {M: {39: 10.50, 49: 15.25}, F: {39: 12.00, 49: 17.00}}
  const genderRates = rates[employee.gender] || {};
  let totalRate = 0;
  
  // Find matching age band for employee
  const ageBands = Object.keys(genderRates).map(Number).sort((a, b) => a - b);
  for (const maxAge of ageBands) {
    if (age <= maxAge) {
      totalRate = genderRates[maxAge];
      break;
    }
  }
  
  // Use highest band if age exceeds all
  if (totalRate === 0 && ageBands.length > 0) {
    totalRate = genderRates[ageBands[ageBands.length - 1]];
  }
  
  // Add rates for covered family members
  if (coverageLevel !== 'employee_only' && familyMembers.length > 0) {
    familyMembers.forEach(member => {
      if (member.date_of_birth && member.gender) {
        const memberAge = calculateAge(member.date_of_birth);
        const memberGenderRates = rates[member.gender] || {};
        
        for (const maxAge of ageBands) {
          if (memberAge <= maxAge) {
            totalRate += (memberGenderRates[maxAge] || 0);
            break;
          }
        }
      }
    });
  }
  
  return totalRate;
}

/**
 * Tobacco-rated pricing from rating table
 */
function calculateTobaccoRated(rates, employee, coverageLevel) {
  if (!employee.date_of_birth) return 0;
  
  const age = calculateAge(employee.date_of_birth);
  
  // Base rates by age
  const baseRates = rates.base_rates || {};
  let rate = 0;
  const ageBands = Object.keys(baseRates).map(Number).sort((a, b) => a - b);
  
  for (const maxAge of ageBands) {
    if (age <= maxAge) {
      rate = baseRates[maxAge];
      break;
    }
  }
  
  if (rate === 0 && ageBands.length > 0) {
    rate = baseRates[ageBands[ageBands.length - 1]];
  }
  
  // Apply tobacco multiplier
  if (employee.tobacco_user && rates.tobacco_multiplier) {
    rate *= rates.tobacco_multiplier;
  }
  
  // Apply family multiplier
  const familyMultipliers = {
    employee_only: 1.0,
    employee_spouse: 1.8,
    employee_children: 1.5,
    family: 2.5
  };
  
  return rate * (familyMultipliers[coverageLevel] || 1.0);
}

/**
 * Geographic pricing from rating table
 */
function calculateGeographic(rates, employee, coverageLevel) {
  // Determine zone based on state or zip - rates structure: {zone_1: 1.0, zone_2: 1.15, zone_3: 1.3}
  const state = employee.state || employee.state_of_residence;
  
  // Default to zone 1 if we can't determine
  const zoneMultiplier = rates.zone_1 || 1.0;
  
  // Base rate assumption
  const baseRate = rates.base_rate || 50;
  
  const familyMultipliers = {
    employee_only: 1.0,
    employee_spouse: 1.8,
    employee_children: 1.5,
    family: 2.5
  };
  
  return baseRate * zoneMultiplier * (familyMultipliers[coverageLevel] || 1.0);
}

/**
 * Get age band for an age
 */
export function getAgeBand(age) {
  if (age <= 39) return 39;
  if (age <= 49) return 49;
  if (age <= 59) return 59;
  return 69;
}